// Options that make a MUI 5 theme look and behave like the given v4 theme, so a component keeps its
// appearance when its imports move from @material-ui/* to @mui/*. Everything here is a value or a
// default that MUI changed between v4 and v5; drop an entry once no component relies on it.
import { fade } from '@material-ui/core/styles';

export const muiV4Compat = (themeV4) => {
  const { palette } = themeV4;
  // v4's default (grey) Button: its own text colour and outline instead of v5's `inherit`, which
  // would take both from the parent.
  const defaultButtonHover = { backgroundColor: fade(palette.text.primary, palette.action.hoverOpacity) };
  const outline = palette.type === 'light' ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.23)';

  return {
    // v5 moved `md` to 900 and `lg` to 1200. Note the separate semantic change that cannot be fixed
    // here: v5's `breakpoints.down(key)` no longer includes `key` itself - v4 `down('sm')` is v5
    // `down('md')`.
    breakpoints: { values: themeV4.breakpoints.values },
    palette: {
      error: palette.error,
      warning: palette.warning,
      info: palette.info,
      success: palette.success,
      text: { secondary: palette.text.secondary, hint: palette.text.hint },
      background: { default: palette.background.default },
      // Backs `color="default"` on Button, which v5 dropped: the contained variant reads it as is,
      // text and outlined are finished by the variants below.
      default: { main: palette.grey[300], dark: palette.grey.A100, contrastText: palette.text.primary },
    },
    components: {
      MuiButton: {
        defaultProps: { color: 'default' },
        variants: [
          {
            props: { variant: 'text', color: 'default' },
            style: { color: palette.text.primary, '&:hover': defaultButtonHover },
          },
          {
            props: { variant: 'outlined', color: 'default' },
            style: {
              color: palette.text.primary,
              borderColor: outline,
              '&:hover': { ...defaultButtonHover, borderColor: outline },
            },
          },
        ],
      },
      MuiCheckbox: { defaultProps: { color: 'secondary' } },
      MuiRadio: { defaultProps: { color: 'secondary' } },
      MuiSwitch: { defaultProps: { color: 'secondary' } },
      MuiTabs: { defaultProps: { indicatorColor: 'secondary', textColor: 'inherit' } },
      MuiTextField: { defaultProps: { variant: 'standard' } },
      MuiFormControl: { defaultProps: { variant: 'standard' } },
      MuiSelect: { defaultProps: { variant: 'standard' } },
      MuiLink: { defaultProps: { underline: 'hover' } },
      // Sizes v5 changed: without these a moved IconButton shrinks, a moved Tab grows and a moved
      // DialogContent gains top/bottom space.
      MuiIconButton: { styleOverrides: { root: { padding: 12 }, sizeSmall: { padding: 3 } } },
      MuiTab: {
        styleOverrides: {
          root: {
            maxWidth: 264,
            minWidth: 72,
            padding: '6px 12px',
            lineHeight: themeV4.typography.button.lineHeight,
            [themeV4.breakpoints.up('sm')]: { minWidth: 160 },
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: '8px 24px',
            '&:first-child': { paddingTop: 20 },
            // v5 removes the top padding of content that follows a title; v4 kept it
            '.v5-MuiDialogTitle-root + &': { paddingTop: 8 },
          },
        },
      },
    },
  };
};
